import React, { useEffect, useState } from 'react'
import { canAdministrativelyCloseJob, isAdministrativelyClosed } from '../lib/administrativeClose'
import { runJobLifecycleAction } from '../lib/jobLifecycle'
import { supabase } from '../lib/supabase'

function fmtDateTime(value) {
  if (!value) return ''
  return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

export default function AdministrativeClosePanel({ profile, job = {}, missingStages = [], onClosed }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setOpen(false)
    setReason('')
    setConfirmed(false)
    setBusy(false)
    setError('')
  }, [job.id])

  if (isAdministrativelyClosed(job)) {
    return (
      <div className="alert alert-info" style={{ marginTop: 12 }}>
        <strong>Administratively closed</strong>
        <div className="fs12" style={{ marginTop: 5 }}>
          {job.administrative_closed_by_name || 'Workshop owner'} · {fmtDateTime(job.administrative_closed_at)}
        </div>
        {job.administrative_close_reason && <div className="fs12" style={{ marginTop: 5 }}>{job.administrative_close_reason}</div>}
        <div className="fs11 text-muted" style={{ marginTop: 5 }}>Incomplete workshop stages remain recorded as incomplete.</div>
      </div>
    )
  }

  if (!canAdministrativelyCloseJob(profile, job) || !missingStages.length) return null

  async function submit() {
    const cleanReason = reason.trim()
    if (cleanReason.length < 10) {
      setError('Add a clear reason of at least 10 characters.')
      return
    }
    if (!confirmed) {
      setError('Confirm that the incomplete stages will remain recorded.')
      return
    }

    setBusy(true)
    setError('')
    try {
      const data = await runJobLifecycleAction(supabase, 'administrative_close', job.id, {
        reason: cleanReason,
        confirmIncomplete: true,
      })
      const nextJob = data?.job || data
      if (nextJob?.id) onClosed?.(nextJob)
      setOpen(false)
    } catch (err) {
      setError(err.message || 'Could not administratively close this job.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      {!open ? (
        <button type="button" className="btn btn-red btn-sm" onClick={() => setOpen(true)}>
          Administrative Close
        </button>
      ) : (
        <div>
          <div className="alert alert-info" style={{ marginBottom: 10 }}>
            <strong>Owner-only exception</strong>
            <div className="fs12" style={{ marginTop: 5 }}>This closes the paid job without marking the following stages complete:</div>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {missingStages.map(stage => <li key={stage} className="fs12">{stage}</li>)}
            </ul>
          </div>
          <div className="field">
            <label>Reason for administrative close</label>
            <textarea
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="Explain why this paid job is being closed with incomplete records..."
              disabled={busy}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10 }}>
            <input type="checkbox" checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={busy} />
            <span className="fs12">I understand the missing stages will remain recorded as incomplete.</span>
          </label>
          {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}
          <div className="action-row" style={{ marginTop: 10 }}>
            <button type="button" className="btn btn-red btn-sm" onClick={submit} disabled={busy || reason.trim().length < 10 || !confirmed}>
              {busy ? 'Closing...' : 'Confirm Administrative Close'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setError('') }} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
